
import React, { useState, useEffect } from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { AppSettings } from '@/types/appSettings';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Form } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import AppNameSection from './settings/AppNameSection';
import GamesPerDaySection from './settings/GamesPerDaySection';
import ImageUploadSection from './settings/ImageUploadSection';
import FormActions from './settings/FormActions';

interface AppSettingsFormProps {
  settings: AppSettings;
  saving: boolean;
  onSave: () => void;
  onSettingsChange: (field: string, value: string | number | null) => void;
  isFormDirty?: boolean;
  saveSuccess?: boolean;
  hasChanges?: boolean;
}

const AppSettingsForm: React.FC<AppSettingsFormProps> = ({ 
  settings, 
  saving, 
  onSave, 
  onSettingsChange,
  isFormDirty = false,
  saveSuccess = false,
  hasChanges = false
}) => {
  const { t } = useTranslationUtils();
  const { toast } = useToast();
  
  // Use controlled form inputs to avoid input overriding issues
  const [formValues, setFormValues] = useState({
    app_name: settings.app_name || '',
    games_allowed_per_day: settings.games_allowed_per_day || 3,
    logo_url: settings.logo_url || null,
    favicon_url: settings.favicon_url || null
  });

  const form = useForm({
    defaultValues: {
      app_name: settings.app_name || '',
      games_allowed_per_day: settings.games_allowed_per_day || 3
    }
  });

  // Update form values when settings change from outside
  useEffect(() => {
    // Only update if settings has changed and it's not during user input
    if (!isFormDirty) {
      setFormValues({
        app_name: settings.app_name || '',
        games_allowed_per_day: settings.games_allowed_per_day || 3,
        logo_url: settings.logo_url || null,
        favicon_url: settings.favicon_url || null
      });
      
      // Update form values for react-hook-form validation
      form.setValue('app_name', settings.app_name || '');
      form.setValue('games_allowed_per_day', settings.games_allowed_per_day || 3);
    }
  }, [settings, form, isFormDirty]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Update local form state
    setFormValues(prev => ({
      ...prev,
      [name]: name === 'games_allowed_per_day' ? parseInt(value) : value
    }));
    
    // Notify parent component of changes
    if (name === 'app_name') {
      onSettingsChange(name, value);
    } else if (name === 'games_allowed_per_day') {
      const numValue = parseInt(value);
      if (!isNaN(numValue) && numValue >= 0) {
        onSettingsChange(name, numValue);
      }
    }
  };

  // Apply local values to the settings when saving
  const handleSave = () => {
    if (!formValues.app_name || formValues.app_name.trim() === '') {
      toast({
        title: t('admin.error', 'Error'),
        description: t('admin.appNameRequired', 'Application name is required'),
        variant: 'destructive',
      });
      return;
    }

    // Update settings with form values
    onSettingsChange('app_name', formValues.app_name);
    onSettingsChange('games_allowed_per_day', formValues.games_allowed_per_day);

    // Call the save function
    onSave();
  };

  // Prevent form reset on submit
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.brandingSettings', 'Branding Settings')}</CardTitle>
        <CardDescription>
          {t('admin.brandingSettingsDescription', 'Customize your application branding elements including name, logo, and favicon.')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Form {...form}>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <AppNameSection 
              form={form}
              value={formValues.app_name}
              onChange={(value) => {
                setFormValues(prev => ({ ...prev, app_name: value }));
                onSettingsChange('app_name', value);
              }}
              handleInputChange={handleInputChange}
            />
            
            <GamesPerDaySection 
              form={form}
              value={formValues.games_allowed_per_day}
              onChange={(value) => {
                setFormValues(prev => ({ ...prev, games_allowed_per_day: value }));
                onSettingsChange('games_allowed_per_day', value);
              }}
              handleInputChange={handleInputChange}
            />
          </form>
        </Form>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ImageUploadSection 
            title={t('admin.logoImage', 'Logo Image')}
            description={t('admin.logoImageDesc', 'This image will be used as your application logo. Recommended size: 512x512px.')}
            existingUrl={formValues.logo_url}
            onUpload={(url) => {
              setFormValues(prev => ({ ...prev, logo_url: url }));
              onSettingsChange('logo_url', url);
            }}
          />
          
          <ImageUploadSection 
            title={t('admin.faviconImage', 'Favicon Image')}
            description={t('admin.faviconImageDesc', 'This icon will appear in browser tabs. Recommended size: 32x32px or 64x64px.')}
            existingUrl={formValues.favicon_url}
            onUpload={(url) => {
              setFormValues(prev => ({ ...prev, favicon_url: url }));
              onSettingsChange('favicon_url', url);
            }}
          />
        </div>
      </CardContent>
      <CardFooter>
        <FormActions 
          saving={saving}
          saveSuccess={saveSuccess}
          onSave={handleSave}
          hasChanges={hasChanges || isFormDirty}
        />
      </CardFooter>
    </Card>
  );
};

export default AppSettingsForm;
